import React from 'react';
import {
  AxisTypeValue,
  Series,
  TooltipFormatterContextObject,
} from 'highcharts';
import styles from './ChartTooltip.module.scss';
import cx from 'classnames';
import Highcharts from 'highcharts';
import { ChartType } from 'Chart';

export interface ChartTooltipProps {
  chartType: ChartType['type'];
  dateFormatString: string;
  tooltipComponent?: React.ReactElement;
  xAxisType: AxisTypeValue;
}

export interface TooltipSeries extends Series {
  legendLine: {
    d: string;
    element: HTMLElement;
  };
  legendSymbol: {
    d?: string;
    element: HTMLElement;
  };
  colorIndex: number;
}

const TooltipHeader = ({
  dateFormatString,
  xAxisType,
  xVal,
}: {
  dateFormatString: string;
  xAxisType: AxisTypeValue;
  xVal: string | number | undefined;
}) => {
  let tooltipTitle = '';

  switch (xAxisType) {
    case 'datetime':
      tooltipTitle = xVal
        ? `${Highcharts.dateFormat(dateFormatString, xVal as number)}`
        : '';
      break;
    case 'linear':
      tooltipTitle = `Day ${xVal}`;
      break;
    default:
      tooltipTitle = `${xVal}`;
      break;
  }
  return <div className={styles.tooltipHeader}>{tooltipTitle}</div>;
};

const LegendLineSVG = ({ pathData }: { pathData: string }) => (
  <path d={pathData} className='highcharts-graph' />
);

// Base stroke width for legend symbol
const STROKE_WIDTH = 1;
// Stroke width with some offset to ensure antialiasing doesn't get clipped (0.1 on each side)
const STROKE_WIDTH_MINUS_ANTIALIASING = STROKE_WIDTH - 0.2;

const LegendSymbolSVG = ({
  legendSymbolElement,
  pathData,
}: {
  legendSymbolElement: HTMLElement;
  pathData?: string;
}) => {
  const symbolRadius =
    parseInt(legendSymbolElement.getAttribute('width') || '') / 2 - 1;
  const centerX =
    parseInt(legendSymbolElement.getAttribute('x') as string) +
    symbolRadius +
    STROKE_WIDTH_MINUS_ANTIALIASING;
  const centerY =
    parseInt(legendSymbolElement.getAttribute('y') as string) +
    symbolRadius +
    STROKE_WIDTH_MINUS_ANTIALIASING;
  return legendSymbolElement.tagName === 'rect' ? (
    <circle
      r={symbolRadius.toString()}
      className='highcharts-point'
      cx={centerX.toString()}
      cy={centerY.toString()}
    />
  ) : (
    <path d={pathData} className='highcharts-point' />
  );
};

export const ChartTooltip = ({
  tooltipComponent,
  chartType,
  xAxisType,
  dateFormatString,
  ...tooltipProps
}: ChartTooltipProps & TooltipFormatterContextObject): JSX.Element => {
  if (tooltipComponent) {
    return React.cloneElement(tooltipComponent, { ...tooltipProps });
  }

  return (
    <div className={cx(styles.tooltipTable, `highcharts-${chartType}-series`)}>
      <TooltipHeader
        xAxisType={xAxisType}
        xVal={tooltipProps.x}
        dateFormatString={dateFormatString}
      />
      {(tooltipProps.points as TooltipFormatterContextObject[]).map(
        (point: TooltipFormatterContextObject, idx: number): JSX.Element => {
          // A couple of the props we use don't have TS support from
          // Highcharts' definitions as of yet, so we wrap the native
          // Series type with those missing props
          const series = point.series as TooltipSeries;
          const legendLine = series.legendLine;
          const legendSymbolElement =
            series.legendSymbol && series.legendSymbol.element;

          return (
            <div className={styles.tooltipRow} key={`tooltip-row-${idx}`}>
              <div className={styles.tooltipColumn}>
                <div className={`highcharts-color-${series.colorIndex}`}>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 24 12'
                    className={styles.tooltipMarkerSymbol}
                  >
                    {legendLine && <LegendLineSVG pathData={legendLine.d} />}
                    {legendSymbolElement && (
                      <LegendSymbolSVG
                        legendSymbolElement={legendSymbolElement}
                        pathData={series.legendSymbol.d}
                      />
                    )}
                  </svg>
                </div>
                <div className={styles.pointSeriesName}>
                  {point.series.name}
                </div>
              </div>
              <div className={styles.tooltipColumn}>
                <span className={styles.pointValue}>
                  {point.y?.toLocaleString()}
                </span>
              </div>
            </div>
          );
        },
      )}
    </div>
  );
};
